/**
 * Shared local HTTP server utility.
 *
 * Starts an ephemeral-port server on 127.0.0.1 that listens for a callback
 * at a configurable path. Used by `auth login` and `claim` commands.
 */

import { createServer } from 'node:http'
import { URL } from 'node:url'

export interface LocalServerOptions<T> {
  /** URL path to listen on (e.g. `/callback`) */
  callbackPath: string
  /** Extract the result from callback query params. Throw to reject. */
  parseParams: (params: URLSearchParams) => T
  /** HTML body to send on success */
  successHtml?: string
  /** HTML body to send on error */
  errorHtml?: string
}

export interface LocalServerHandle<T> {
  port: number
  resultPromise: Promise<T>
  shutdown: () => void
}

export function startLocalServer<T>(
  options: LocalServerOptions<T>
): Promise<LocalServerHandle<T>> {
  const {
    callbackPath,
    parseParams,
    successHtml = `<html><body><p>Success. You can close this tab.</p><script>window.close()</script></body></html>`,
    errorHtml = `<html><body><p>Something went wrong. Please try again.</p></body></html>`,
  } = options

  return new Promise((resolveStart) => {
    let resolveResult: (result: T) => void
    let rejectResult: (error: Error) => void

    const resultPromise = new Promise<T>((resolve, reject) => {
      resolveResult = resolve
      rejectResult = reject
    })

    const server = createServer((req, res) => {
      const url = new URL(req.url ?? `/`, `http://localhost`)

      if (url.pathname === callbackPath) {
        try {
          const result = parseParams(url.searchParams)
          res.writeHead(200, { 'Content-Type': `text/html` })
          res.end(successHtml)
          resolveResult(result)
        } catch (err) {
          res.writeHead(400, { 'Content-Type': `text/html` })
          res.end(errorHtml)
          rejectResult(err instanceof Error ? err : new Error(String(err)))
        }
      } else {
        res.writeHead(404)
        res.end()
      }
    })

    const shutdown = () => {
      server.close()
    }

    server.listen(0, `127.0.0.1`, () => {
      const addr = server.address()
      const port = typeof addr === `object` && addr ? addr.port : 0
      resolveStart({ port, resultPromise, shutdown })
    })
  })
}
